import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, Clock, MapPin, User, Stethoscope, Building2, Plus, X, Check, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { API } from "../App";

const Bookings = ({ user }) => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }
    fetchBookings();
    fetchProviders();
  }, [user, navigate]);

  const fetchBookings = async () => {
    try {
      const response = await fetch(`${API}/bookings`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setBookings(data.bookings || []);
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProviders = async () => {
    try {
      const response = await fetch(`${API}/providers`);
      if (response.ok) {
        const data = await response.json();
        setProviders(data.doctors || []);
      }
    } catch (error) {
      console.error("Error fetching providers:", error);
    }
  };

  const handleBooking = async () => {
    if (!selectedProvider || !selectedDate || !selectedTime) {
      toast.error("Please fill all fields");
      return;
    }

    setBookingLoading(true);
    try {
      const response = await fetch(`${API}/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          provider_type: selectedProvider.type,
          provider_name: selectedProvider.name,
          provider_id: selectedProvider.provider_id,
          appointment_date: selectedDate,
          appointment_time: selectedTime,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success("Booking confirmed!", {
          description: `Your appointment with ${selectedProvider.name} is confirmed.`,
        });
        setShowBookingDialog(false);
        fetchBookings();
        resetBookingForm();
      } else {
        throw new Error("Booking failed");
      }
    } catch (error) {
      toast.error("Failed to book appointment. Please try again.");
    } finally {
      setBookingLoading(false);
    }
  };

  const cancelBooking = async (bookingId) => {
    try {
      const response = await fetch(`${API}/bookings/${bookingId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Booking cancelled");
        fetchBookings();
      }
    } catch (error) {
      toast.error("Failed to cancel booking");
    }
  };

  const resetBookingForm = () => {
    setSelectedProvider(null);
    setSelectedDate("");
    setSelectedTime("");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "bg-green-100 text-green-700";
      case "cancelled":
        return "bg-red-100 text-red-700";
      case "completed":
        return "bg-blue-100 text-blue-700";
      default:
        return "bg-slate-100 text-slate-700";
    }
  };

  const timeSlots = [
    "09:00 AM", "09:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
    "02:00 PM", "02:30 PM", "03:00 PM", "03:30 PM", "04:00 PM", "04:30 PM",
    "05:00 PM", "05:30 PM", "06:00 PM"
  ];

  return (
    <div className="min-h-screen pb-24 bg-gradient-to-br from-blue-50 via-white to-blue-100">
      {/* Header */}
      <div className="glass-panel sticky top-0 z-40 px-4 py-4 border-b border-white/30">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading font-semibold text-slate-800">My Bookings</h1>
              <p className="text-xs text-slate-500">Manage your appointments</p>
            </div>
          </div>

          <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
            <DialogTrigger asChild>
              <Button 
                size="sm" 
                className="rounded-full bg-primary-500 hover:bg-primary-600"
                data-testid="new-booking-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                New
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md mx-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">Book Appointment</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                {/* Provider Selection */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Select Doctor</label>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {providers.map((provider) => (
                      <div
                        key={provider.provider_id}
                        className={`p-3 rounded-xl border cursor-pointer transition-all ${
                          selectedProvider?.provider_id === provider.provider_id
                            ? "border-primary-500 bg-primary-50"
                            : "border-slate-200 hover:border-primary-300"
                        }`}
                        onClick={() => setSelectedProvider(provider)}
                        data-testid={`provider-${provider.provider_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-primary-500" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-slate-800 text-sm">{provider.name}</p>
                            <p className="text-xs text-slate-500">{provider.specialization}</p>
                          </div>
                          <span className="text-sm font-semibold text-primary-600">₹{provider.fee}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Date Selection */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Select Date</label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full p-3 rounded-xl border border-slate-200 focus:border-primary-500 outline-none"
                    data-testid="date-input"
                  />
                </div>

                {/* Time Selection */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Select Time</label>
                  <div className="grid grid-cols-3 gap-2 max-h-32 overflow-y-auto">
                    {timeSlots.map((time) => (
                      <button
                        key={time}
                        onClick={() => setSelectedTime(time)}
                        className={`p-2 rounded-lg text-xs font-medium transition-all ${
                          selectedTime === time
                            ? "bg-primary-500 text-white"
                            : "bg-slate-100 text-slate-600 hover:bg-primary-100"
                        }`}
                        data-testid={`time-${time.replace(/\s/g, '-')}`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Book Button */}
                <Button
                  className="w-full rounded-full bg-primary-500 hover:bg-primary-600"
                  onClick={handleBooking}
                  disabled={bookingLoading || !selectedProvider || !selectedDate || !selectedTime}
                  data-testid="confirm-booking-btn"
                >
                  {bookingLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  Confirm Booking
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="glass-card rounded-2xl p-4 animate-pulse">
                <div className="h-4 bg-slate-200 rounded w-1/3 mb-3"></div>
                <div className="h-3 bg-slate-200 rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-slate-200 rounded w-1/4"></div>
              </div>
            ))}
          </div>
        ) : bookings.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-12"
          >
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-primary-500" />
            </div>
            <h3 className="font-heading text-lg font-semibold text-slate-800 mb-2">No bookings yet</h3>
            <p className="text-slate-500 mb-6">Book your first appointment with a doctor</p>
            <Button
              onClick={() => setShowBookingDialog(true)}
              className="rounded-full bg-primary-500 hover:bg-primary-600"
              data-testid="book-first-appointment-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Book Appointment
            </Button>
          </motion.div>
        ) : (
          <div className="space-y-4">
            <AnimatePresence>
              {bookings.map((booking, index) => (
                <motion.div
                  key={booking.booking_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-card rounded-2xl p-4 booking-card"
                  data-testid={`booking-${booking.booking_id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                        <Stethoscope className="w-6 h-6 text-primary-500" />
                      </div>
                      <div>
                        <h3 className="font-medium text-slate-800">{booking.provider_name}</h3>
                        <p className="text-sm text-slate-500 capitalize">{booking.provider_type}</p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(booking.status)}`}>
                      {booking.status}
                    </span>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4 text-primary-500" />
                      {booking.appointment_date}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4 text-primary-500" />
                      {booking.appointment_time}
                    </div>
                  </div>

                  {booking.status === "confirmed" && (
                    <div className="mt-4 flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 rounded-full text-xs"
                        onClick={() => navigate("/nearby")}
                      >
                        <MapPin className="w-3 h-3 mr-1" />
                        Get Directions
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-full text-xs text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => cancelBooking(booking.booking_id)}
                        data-testid={`cancel-booking-${booking.booking_id}`}
                      >
                        <X className="w-3 h-3 mr-1" />
                        Cancel
                      </Button>
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};

export default Bookings;
